// Pure OT-approval policy logic — no Firestore, no React.
// Shared by OTApprovalPage (UI disabling), AddTimeRecordModal (status on
// create/edit), and mirrored by otApprovalService (authoritative transaction).
// Kept dependency-free so it can be unit-tested in jsdom (Tier 1).

export const MAKER_CHECKER_TOOLTIP =
  "Maker-checker is enabled. Ask another approver, or change the setting in Settings → Attendance Policy.";
export const LOCK_COMPLETED_TOOLTIP = "Locked by completed payroll";
export const LOCK_PENDING_TOOLTIP = "Locked by pending payroll approval";

/**
 * The "maker" of a time record = whoever last set the OT value.
 * updatedBy if the record has been edited, else createdBy on first creation.
 */
export const getMaker = (record) => record?.updatedBy ?? record?.createdBy ?? null;

/**
 * Reason the approve/reject buttons should be disabled for a record, or null
 * if the current user may act on it.
 *
 * - Payroll locks block action in BOTH policy modes.
 * - The maker is blocked ONLY under 'maker-checker'. Under 'self-approval'
 *   the maker may approve their own entry.
 *
 * @param {object} args
 * @param {object} args.record - { updatedBy, createdBy }
 * @param {string} args.currentUserId - current user's userDocId
 * @param {string} args.approvalType - 'self-approval' | 'maker-checker'
 * @param {string|null} [args.lockStatus] - derived DTR lock status for this record
 *   ('completed' | 'pending-approval' | 'draft' | null), from dtrLockDerivation
 *   (PRD_DtrWriteModel C1/C6) — replaces the former record.payrollLock read.
 * @returns {string|null}
 */
export const getOtDisableReason = ({ record, currentUserId, approvalType, lockStatus = null }) => {
  if (approvalType === "maker-checker" && getMaker(record) === currentUserId) {
    return MAKER_CHECKER_TOOLTIP;
  }
  if (lockStatus === "completed") return LOCK_COMPLETED_TOOLTIP;
  if (lockStatus === "pending-approval") return LOCK_PENDING_TOOLTIP;
  return null;
};

/**
 * Approval status for a DTR's OT hours at create/edit time.
 * Mirrors the historical inline logic exactly:
 *   auto-approve only when threshold > 0 AND otHours <= threshold.
 *
 * @param {number} overtimeHours
 * @param {number} threshold
 * @returns {'auto-approved'|'pending'|null} null when there is no OT to approve
 */
export const evaluateOtStatus = (overtimeHours, threshold) => {
  if (!overtimeHours || overtimeHours <= 0) return null;
  const t = Number(threshold) || 0;
  return t > 0 && overtimeHours <= t ? "auto-approved" : "pending";
};
